import { useEffect, useState } from 'react';
import {
  type UpdateDirectAssignmentContext,
  type DynamicAssignmentEntry,
} from '@scale/genai-compass-shared/cards/update_direct_assignment';
import { useCompassCard } from '@scale/compass-ui/store';
import _ from 'lodash';
import { columnsFromRecord } from '@scale/compass-shared/utils';
import { getWorkerIdColumn } from 'react-scale/corp/views/DataCompassCardsV2/action/UpdateMetadataCard/utils';
import { useUser } from 'react-scale/components/ui/UserContext';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { ASSIGNMENT_RANK_OPTIONS } from './constants';

const useUpdateDirectAssignmentCard = (index: number) => {
  const { card, updateCardContext, updateCardCanSubmit } = useCompassCard(index);
  const { retrievedOutput } = useCompassCard(index - 1);
  const user = useUser();
  const context = card.defaultContext as UpdateDirectAssignmentContext;
  const isLoading = false;
  const data = retrievedOutput?.response?.data ?? [];
  // const columns = retrievedOutput?.response?.schema ?? [];

  // Get columns from the previous card's output
  const availableColumns = columnsFromRecord(retrievedOutput?.response?.data) || [];

  const workerIdColumn = getWorkerIdColumn(retrievedOutput);
  const [numWorkers, setNumWorkers] = useState(0);

  const isDynamicCardVersion =
    useUnleashFeatureFlag('dynamic-da-card', { userId: user.id }, false) ?? false;

  /**
   * Can only submit if:
   * - there's a non-empty amount of workers
   * - all direct assignments have a projectId or if remove all assignments is checked
   * - capacity request ID is present
   */
  const validateFieldsAndMarkCanSubmit = (
    updatedContext: UpdateDirectAssignmentContext,
    updatedNumWorkers?: number,
  ) => {
    if (
      (updatedNumWorkers !== undefined ? updatedNumWorkers > 0 : numWorkers > 0) &&
      ((updatedContext.assignments.length > 0 &&
        updatedContext.assignments.every(assignment => !_.isEmpty(assignment.projectId))) ||
        updatedContext.removeAllAssignments) &&
      updatedContext.capacityRequestId
    ) {
      updateCardCanSubmit(true);
    } else {
      updateCardCanSubmit(false);
    }
  };

  const setAssignments = (assignments: DynamicAssignmentEntry[]) => {
    const updatedContext: UpdateDirectAssignmentContext = {
      ...context,
      assignments,
    };
    updateCardContext(updatedContext);
    validateFieldsAndMarkCanSubmit(updatedContext);
  };

  const setCapacityRequestId = (capacityRequestId: string) => {
    const updatedContext: UpdateDirectAssignmentContext = {
      ...context,
      capacityRequestId,
    };
    updateCardContext(updatedContext);
    validateFieldsAndMarkCanSubmit(updatedContext);
  };

  const setOverwrite = (overwrite: boolean) => {
    const updatedContext: UpdateDirectAssignmentContext = {
      ...context,
      overwrite,
    };
    updateCardContext(updatedContext);
  };

  const setRemoveAllAssignments = (removeAllAssignments: boolean) => {
    const updatedContext: UpdateDirectAssignmentContext = {
      ...context,
      removeAllAssignments,
    };
    updateCardContext(updatedContext);
    validateFieldsAndMarkCanSubmit(updatedContext);
  };

  const setColumnIdWithWorkerIds = (columnIdWithWorkerIds: string) => {
    const columnId = columnIdWithWorkerIds;
    const updatedContext: UpdateDirectAssignmentContext = {
      ...context,
      columnIdWithWorkerIds: columnId,
    };
    updateCardContext(updatedContext);

    if (!columnId) {
      setNumWorkers(0);
      updateCardCanSubmit(false);
      return;
    }

    const workerRows = data.filter(row => row[columnId]);
    setNumWorkers(workerRows.length);
    validateFieldsAndMarkCanSubmit(updatedContext, workerRows.length);
  };

  const setAssignmentRank = (assignmentRank: string) => {
    const selectedIndex = ASSIGNMENT_RANK_OPTIONS.findIndex(option => option.id === assignmentRank);
    let assignmentRankValue: number | undefined;

    if (selectedIndex === -1 || selectedIndex === 0) {
      // -1 means not found, 0 means "push" option - both should be undefined
      assignmentRankValue = undefined;
    } else {
      // Subtract 1 because "push" is at index 0, so "top" at index 1 should map to 0, etc.
      assignmentRankValue = selectedIndex - 1;
    }

    const updatedContext = {
      ...context,
      assignmentRank: assignmentRankValue,
    };
    updateCardContext(updatedContext);
  };

  useEffect(() => {
    if (workerIdColumn) {
      setColumnIdWithWorkerIds(workerIdColumn);
    }
  }, [workerIdColumn]);

  return {
    assignments: context.assignments,
    setAssignments,
    capacityRequestId: context.capacityRequestId,
    setCapacityRequestId,
    assignmentRank: context.assignmentRank,
    setAssignmentRank,
    overwrite: context.overwrite,
    setOverwrite,
    removeAllAssignments: context.removeAllAssignments,
    setRemoveAllAssignments,
    columnIdWithWorkerIds: context.columnIdWithWorkerIds,
    setColumnIdWithWorkerIds,
    availableColumns,
    numWorkers,
    isLoading,
    isDynamicCardVersion,
    data,
    card,
  };
};
export default useUpdateDirectAssignmentCard;
